<template>
  <div class="login-view">
    <div class="card login-card">
      <h1>Welcome to GenAI Edu</h1>
      <p class="subtitle">Please sign in to continue</p>
      
      <div v-if="step === 'email'" class="login-form">
        <div class="form-group">
          <label for="email">Your Email Address</label>
          <input 
            type="email" 
            id="email" 
            v-model="email" 
            placeholder="Enter your email"
            @keyup.enter="requestToken"
            :disabled="isLoading">
          <p class="form-hint">We'll send you a one-time login code</p>
        </div>
        
        <button 
          class="btn btn-primary login-btn" 
          @click="requestToken"
          :disabled="!isValidEmail || isLoading">
          <span v-if="isLoading" class="loader"></span>
          <span v-else>Get Login Code</span>
        </button>
        
        <!-- Development mode shortcut -->
        <div class="dev-mode" v-if="isDevelopment">
          <button class="link-btn dev-button" @click="useTestMode">
            <span class="material-icons">developer_mode</span>
            Use Test Mode
          </button>
        </div>
      </div>
      
      <div v-if="step === 'token'" class="login-form">
        <div class="form-group">
          <label for="token">Enter Login Code</label>
          <input 
            type="text" 
            id="token" 
            v-model="token" 
            placeholder="Enter the code from your email"
            @keyup.enter="verifyToken"
            :disabled="isLoading"
            class="token-input"
            maxlength="6">
          <p class="form-hint">
            We sent a code to {{ maskEmail(email) }}
            <button class="link-btn" @click="step = 'email'">Change email</button>
          </p>
        </div>
        
        <button 
          class="btn btn-primary login-btn" 
          @click="verifyToken"
          :disabled="token.length < 6 || isLoading">
          <span v-if="isLoading" class="loader"></span>
          <span v-else>Sign In</span>
        </button>
      </div>
      
      <div v-if="errorMessage" class="error-message">
        <span class="material-icons">error</span>
        {{ errorMessage }}
      </div>
    </div>
  </div>
</template>

<script>
import apiService from '@/services/api'
import { generateId } from '@/utils/helpers'

export default {
  name: 'LoginView',
  data() {
    return {
      step: 'email',
      email: '',
      token: '',
      isLoading: false,
      errorMessage: '',
      sessionId: '',
      isDevelopment: process.env.NODE_ENV === 'development'
    }
  },
  computed: {
    isValidEmail() {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      return emailRegex.test(this.email)
    }
  },
  created() {
    // Check if the user is already logged in
    const session = this.getSession()
    if (session && session.expiresAt > Date.now()) {
      this.$router.push('/')
    }
  },
  methods: {
    requestToken() {
      if (!this.isValidEmail) return
      
      this.isLoading = true
      this.errorMessage = ''
      
      console.log(`[DEBUG] Attempting to request token for email: ${this.email}`)
      
      apiService.requestLoginToken(this.email)
        .then(response => {
          console.log('[DEBUG] Login request successful:', response.data);
          this.step = 'token'
          this.sessionId = response.data.sessionId || generateId()
          
          // For development, show the token in console (assuming mock mode is enabled on backend)
          console.log('[DEBUG] Check server console for the login token');
        })
        .catch(error => {
          console.error('[DEBUG] Login Error:', error)
          
          // More detailed error logging
          if (error.response) {
            console.error('[DEBUG] Error status:', error.response.status)
            console.error('[DEBUG] Error data:', error.response.data)
          }
          
          this.errorMessage = error.message || 'Failed to send login code. Please try again.'
        })
        .finally(() => {
          this.isLoading = false
        })
    },
    
    verifyToken() {
      if (this.token.length < 6) return
      
      this.isLoading = true
      this.errorMessage = ''
      
      console.log(`[DEBUG] Attempting to verify token for email: ${this.email}, sessionId: ${this.sessionId}`)
      
      apiService.verifyLoginToken(this.email, this.token, this.sessionId)
        .then(response => {
          console.log('[DEBUG] Token verification successful:', response.data)
          
          // Save session details to localStorage
          const session = {
            userId: response.data.userId,
            email: this.email,
            token: response.data.token,
            expiresAt: Date.now() + (24 * 60 * 60 * 1000) // 24 hours expiry
          }
          
          localStorage.setItem('session', JSON.stringify(session))
          
          // Redirect to chat
          this.$router.push('/')
        })
        .catch(error => {
          console.error('[DEBUG] Verification Error:', error)
          
          // More detailed error logging
          if (error.response) {
            console.error('[DEBUG] Error status:', error.response.status)
            console.error('[DEBUG] Error data:', error.response.data)
          }
          
          this.errorMessage = error.message || 'Invalid login code. Please try again.'
        })
        .finally(() => {
          this.isLoading = false
        })
    },
    
    maskEmail(email) {
      if (!email) return ''
      
      const [username, domain] = email.split('@')
      const maskedUsername = username.charAt(0) + 
        '*'.repeat(Math.min(5, username.length - 2)) + 
        username.charAt(username.length - 1)
      
      return `${maskedUsername}@${domain}`
    },
    
    getSession() {
      const sessionStr = localStorage.getItem('session')
      return sessionStr ? JSON.parse(sessionStr) : null
    },
    
    // Development test mode shortcut
    useTestMode() {
      // Create a test session
      const testSession = {
        userId: 'test_user_' + generateId(),
        email: 'test@example.com',
        token: 'test_token_' + generateId(),
        expiresAt: Date.now() + (24 * 60 * 60 * 1000) // 24 hours expiry
      }
      
      localStorage.setItem('session', JSON.stringify(testSession))
      
      // Redirect to chat
      this.$router.push('/')
    }
  }
}
</script>

<style scoped>
.login-view {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: calc(100vh - 150px);
  padding: 20px;
}

.login-card {
  width: 100%;
  max-width: 450px;
  padding: 40px;
  text-align: center;
  background-color: var(--background-card);
  border-radius: 12px;
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.3);
}

h1 {
  color: var(--primary-light);
  margin-bottom: 8px;
}

.subtitle {
  color: var(--text-secondary);
  margin-bottom: 32px;
}

.login-form {
  text-align: left;
}

.form-group {
  margin-bottom: 24px;
}

label {
  display: block;
  margin-bottom: 8px;
  color: var(--text-primary);
  font-weight: 500;
}

input {
  width: 100%;
  padding: 12px 16px;
  border-radius: 8px;
  border: 1px solid var(--border-color);
  background-color: var(--background-dark);
  color: var(--text-primary);
  font-size: 16px;
  transition: border-color 0.3s;
}

input:focus {
  border-color: var(--primary-color);
  outline: none;
}

.form-hint {
  margin-top: 8px;
  font-size: 14px;
  color: var(--text-secondary);
}

.login-btn {
  width: 100%;
  padding: 12px;
  font-size: 16px;
  border-radius: 8px;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 48px;
}

.error-message {
  margin-top: 24px;
  padding: 12px;
  background-color: rgba(229, 57, 53, 0.1);
  border-left: 4px solid var(--error-color);
  color: var(--error-color);
  display: flex;
  align-items: center;
  text-align: left;
  border-radius: 4px;
}

.error-message .material-icons {
  margin-right: 8px;
}

.token-input {
  font-size: 24px;
  letter-spacing: 4px;
  text-align: center;
}

.link-btn {
  background: none;
  border: none;
  color: var(--primary-color);
  text-decoration: underline;
  cursor: pointer;
  padding: 0;
  margin-left: 8px;
}

.loader {
  width: 20px;
  height: 20px;
  border: 2px solid var(--text-on-primary);
  border-bottom-color: transparent;
  border-radius: 50%;
  display: inline-block;
  animation: rotation 1s linear infinite;
}

@keyframes rotation {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.dev-mode {
  margin-top: 16px;
  text-align: center;
  border-top: 1px dashed var(--border-color);
  padding-top: 16px;
}

.dev-button {
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto;
  color: var(--text-secondary);
}

.dev-button .material-icons {
  margin-right: 8px;
  font-size: 18px;
}

.dev-button:hover {
  color: var(--primary-color);
}
</style>
